/**
 * Devanagari script utilities: akshara (orthographic syllable) segmentation,
 * Unicode normalisation for comparison, and IAST transliteration.
 *
 * Normalisation removes only differences that never change grammar
 * (Unicode composition, invisible joiners, a typed colon for visarga,
 * a pada-final anusvara standing for म्). It never merges distinct
 * letters, vowel lengths or endings.
 */

const CONS = 'क-हक़-य़ॸ-ॿ'
const NUKTA = '़'
const VIRAMA = '्'
const MATRA = 'ऺऻा-ौॎॏॕ-ॗॢॣ'
const MODS = 'ऀ-ः॑-॔'
const INDEP = 'ऄ-औॠॡॲ-ॷ'

const AKSHARA_SOURCE =
    `(?:[${CONS}]${NUKTA}?${VIRAMA}[‌‍]?)*[${CONS}]${NUKTA}?(?:[${MATRA}]|${VIRAMA}(?![${CONS}]))?[${MODS}]*` +
    `|[${INDEP}][${MODS}]*` +
    `|[\\s\\S]`

/** Split text into aksharas so that no animation or highlight ever cuts a conjunct or matra apart. */
export const aksharas = (text: string): string[] => {
    const re = new RegExp(AKSHARA_SOURCE, 'gu')
    return text.normalize('NFC').match(re) ?? []
}

const DEVANAGARI_RE = /[ऀ-ॿ]/

export const hasDevanagari = (text: string): boolean => DEVANAGARI_RE.test(text)

/** Canonical form for display-safe comparison. */
export const normalizeSanskrit = (input: string): string => {
    return input
        .normalize('NFC')
        .replace(/[​‌‍­﻿]/g, '')
        .replace(/([ऀ-ॿ]):/g, '$1ः')
        .replace(/\|\|/g, '॥')
        .replace(/\|/g, '।')
        .replace(/\s+/g, ' ')
        .trim()
}

const PUNCT_RE = /[।॥,.;!?"'‘’“”()\[\]{}—–-]/g

/**
 * Key used to look a single word up in the form index.
 * A pada-final anusvara is the sentence-internal spelling of म्, so पुस्तकं and पुस्तकम् share a key.
 */
export const lookupKey = (word: string): string => {
    let w = normalizeSanskrit(word).replace(PUNCT_RE, '').replace(/^हे\s+/, '').trim()

    if (w.endsWith('ं')) w = w.slice(0, -1) + 'म्'

    return w
}

export const WORD_SPLIT_RE = /(\s+|[।॥,;!?"‘’“”()—–])/

/** Split a sentence into word and separator tokens (separators preserved for re-rendering). */
export const tokenizeSentence = (sentence: string): Array<{ text: string; isWord: boolean }> => {
    return normalizeSanskrit(sentence)
        .split(WORD_SPLIT_RE)
        .filter(part => part.length > 0)
        .map(part => ({ text: part, isWord: hasDevanagari(part) && !/^[।॥]+$/.test(part) }))
}

const INDEP_VOWELS: Record<string, string> = {
    'अ': 'a',
    'आ': 'ā',
    'इ': 'i',
    'ई': 'ī',
    'उ': 'u',
    'ऊ': 'ū',
    'ऋ': 'ṛ',
    'ॠ': 'ṝ',
    'ऌ': 'ḷ',
    'ॡ': 'ḹ',
    'ए': 'e',
    'ऐ': 'ai',
    'ओ': 'o',
    'औ': 'au'
}

const MATRAS: Record<string, string> = {
    'ा': 'ā',
    'ि': 'i',
    'ी': 'ī',
    'ु': 'u',
    'ू': 'ū',
    'ृ': 'ṛ',
    'ॄ': 'ṝ',
    'ॢ': 'ḷ',
    'ॣ': 'ḹ',
    'े': 'e',
    'ै': 'ai',
    'ो': 'o',
    'ौ': 'au'
}

const CONSONANTS: Record<string, string> = {
    'क': 'k',
    'ख': 'kh',
    'ग': 'g',
    'घ': 'gh',
    'ङ': 'ṅ',
    'च': 'c',
    'छ': 'ch',
    'ज': 'j',
    'झ': 'jh',
    'ञ': 'ñ',
    'ट': 'ṭ',
    'ठ': 'ṭh',
    'ड': 'ḍ',
    'ढ': 'ḍh',
    'ण': 'ṇ',
    'त': 't',
    'थ': 'th',
    'द': 'd',
    'ध': 'dh',
    'न': 'n',
    'प': 'p',
    'फ': 'ph',
    'ब': 'b',
    'भ': 'bh',
    'म': 'm',
    'य': 'y',
    'र': 'r',
    'ल': 'l',
    'ळ': 'ḷ',
    'व': 'v',
    'श': 'ś',
    'ष': 'ṣ',
    'स': 's',
    'ह': 'h'
}

const OTHERS: Record<string, string> = {
    'ं': 'ṃ',
    'ः': 'ḥ',
    'ँ': 'm̐',
    'ऽ': '’',
    '।': '.',
    '॥': '.',
    'ॐ': 'oṃ',
    '०': '0',
    '१': '1',
    '२': '2',
    '३': '3',
    '४': '4',
    '५': '5',
    '६': '6',
    '७': '7',
    '८': '8',
    '९': '9'
}

/** Devanagari to IAST. Every consonant without a virama or matra carries its inherent a (no schwa deletion in Sanskrit). */
export const toIAST = (input: string): string => {
    const text = input.normalize('NFC').replace(/[‌‍]/g, '')
    const chars = Array.from(text)
    let out = ''

    for (let i = 0; i < chars.length; i++) {
        const ch = chars[i]
        const consonant = CONSONANTS[ch]

        if (consonant !== undefined) {
            out += consonant

            let next = chars[i + 1]

            if (next === NUKTA) {
                i++
                next = chars[i + 1]
            }

            if (next === VIRAMA) {
                i++
            }
            else if (next !== undefined && MATRAS[next] !== undefined) {
                out += MATRAS[next]
                i++
            }
            else {
                out += 'a'
            }

            continue
        }

        if (INDEP_VOWELS[ch] !== undefined) {
            out += INDEP_VOWELS[ch]
            continue
        }

        if (OTHERS[ch] !== undefined) {
            out += OTHERS[ch]
            continue
        }

        if (ch === NUKTA || ch === VIRAMA) continue

        out += ch
    }

    return out.replace(/\s+\./g, '.')
}

/** Strip IAST diacritics for forgiving search (paṭhati -> pathati). */
export const asciiFold = (text: string): string => {
    return text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
}

/** Akshara-level edit distance, used for approximate speaking feedback. */
export const aksharaDistance = (a: string, b: string): number => {
    const x = aksharas(lookupKey(a))
    const y = aksharas(lookupKey(b))
    const dp: number[][] = Array.from({ length: x.length + 1 }, () => new Array<number>(y.length + 1).fill(0))

    for (let i = 0; i <= x.length; i++) dp[i][0] = i
    for (let j = 0; j <= y.length; j++) dp[0][j] = j

    for (let i = 1; i <= x.length; i++) {
        for (let j = 1; j <= y.length; j++) {
            const cost = x[i - 1] === y[j - 1] ? 0 : 1
            dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost)
        }
    }

    return dp[x.length][y.length]
}

/** Similarity 0..1 between two Sanskrit strings at akshara level. */
export const aksharaSimilarity = (a: string, b: string): number => {
    const len = Math.max(aksharas(lookupKey(a)).length, aksharas(lookupKey(b)).length, 1)
    return Math.max(0, 1 - aksharaDistance(a, b) / len)
}

/**
 * Index (in aksharas) of the akshara that contains the given code-unit offset.
 * Used to highlight an ending without ever splitting a cluster.
 */
export const aksharaIndexAt = (word: string, offset: number): number => {
    const parts = aksharas(word)
    let pos = 0

    for (let i = 0; i < parts.length; i++) {
        if (offset < pos + parts[i].length) return i
        pos += parts[i].length
    }

    return parts.length - 1
}

/** Split a word into [unchanged prefix, highlighted suffix] at akshara boundaries. */
export const splitForHighlight = (word: string, stemLength: number): [string, string] => {
    if (stemLength <= 0) return ['', word]
    if (stemLength >= word.length) return [word, '']

    const parts = aksharas(word)
    const index = aksharaIndexAt(word, stemLength)

    return [parts.slice(0, index).join(''), parts.slice(index).join('')]
}

/** Longest common prefix length in code units (used to find where an ending begins). */
export const commonPrefixLength = (a: string, b: string): number => {
    let i = 0
    while (i < a.length && i < b.length && a[i] === b[i]) i++
    return i
}

const VALID_SEQUENCE_ERRORS: Array<[RegExp, string]> = [
    [/््/, 'double virama'],
    [/्[ा-ौ]/, 'matra after virama'],
    [/(^|[^क-हक़-य़़])[ा-ौ]/, 'matra without consonant'],
    [/[ा-ौ][ा-ौ]/, 'two matras'],
    [/[a-zA-Z]/, 'latin letters']
]

/** Returns a list of structural problems in a Devanagari string (empty when well formed). */
export const scriptProblems = (word: string): string[] => {
    return VALID_SEQUENCE_ERRORS.filter(([re]) => re.test(word)).map(([, label]) => label)
}
